import React from 'react';
import { ResearchState } from '@/contexts/research/ResearchContext';
import ResearchParameterControls from './ResearchParameterControls';
import ResearchPromptField from './ResearchPromptField';
import GenerateButton from './GenerateButton';

interface ResearchAssistantContentProps {
  state: ResearchState;
  handlers: {
    handleTypeChange: (type: string | null) => void; // Updated to support null
    handleExpandType: (type: string | null) => void;
    handleFiltersChange: (filters: Record<string, string>) => void; // Keep for interface compatibility
    handleTimeframeChange: (value: string) => void;
    handleCountryChange: (value: string) => void;
    handleSourceTypeChange: (value: string) => void; // Updated from handleSourceQualityChange
    handlePromptChange: (value: string) => void;
    handleSimpleModeToggle: (isSimple: boolean) => void;
  };
  onGenerate: () => void;
  isReady: boolean;
  isLoading: boolean;
}

const ResearchAssistantContent: React.FC<ResearchAssistantContentProps> = React.memo(({
  state,
  handlers,
  onGenerate,
  isReady,
  isLoading,
}) => {
  return (
    <div className="space-y-6">
      {/* Step 1: Research Prompt */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium mb-1 text-purple-600 dark:text-purple-400">
          Research Description
        </h3>
        <div className="stable-prompt-container">
          <ResearchPromptField
            value={state.researchPrompt}
            onChange={handlers.handlePromptChange}
            researchType={state.selectedType || ''}
          />
        </div>
      </div>
      
      {/* Step 2: Research Parameters (includes Research Type dropdown and filters) */}
      <div className="space-y-3">
        <ResearchParameterControls
          timeframe={state.timeframe}
          country={state.country}
          sourceType={state.sourceType} // Updated from sourceQuality
          selectedType={state.selectedType || undefined}
          filters={{}} // Dynamic filters removed
          isSimpleMode={state.isSimpleMode}
          onTimeframeChange={handlers.handleTimeframeChange}
          onCountryChange={handlers.handleCountryChange}
          onSourceTypeChange={handlers.handleSourceTypeChange} // Updated from onSourceQualityChange
          onFiltersChange={handlers.handleFiltersChange} // Keep for interface compatibility
          onSimpleModeToggle={handlers.handleSimpleModeToggle}
          onTypeChange={handlers.handleTypeChange}
        />
      </div>
      
      {/* Generate Button */}
      <GenerateButton
        isReady={isReady}
        isLoading={isLoading}
        onGenerate={onGenerate}
      />
    </div>
  );
});

ResearchAssistantContent.displayName = 'ResearchAssistantContent';

export default ResearchAssistantContent;
